import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cultureHover,
  getCultureGrossMargin,
  loadResultMapTabData,
  parcelHoverCultureOpacity,
  sortList,
} from 'utils/Map/MapTabUtils';
import { getColorByName } from 'utils/culturesColors';
import { ExploitationContext } from 'utils/context';
import YearsMapTab from './YearsMapTab';
import { getPercentage } from 'utils/tools_functions';
import { ISuggestYears } from '../Interface/Simulation/ISuggestYears';
import { IParcelSimu } from '../Interface/Simulation/IParcelSimu';
import { IExploitationContext } from '../Interface/Context/IExploitationContext';
import { cultureAndOtherValue } from '../Interface/Simulation/ICultureAndOtherValue';
import './CulturesMapTab.style.scss';
import bioLogo from 'assets/images/bio.png';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

const MapTab = ({
  data,
  header,
  isDynamic,
  year,
  setYear,
  minYear,
  maxYear,
  yearIndex = null,
  suggestions = null,
  parcelHoverCultureName = null,
  parentCallback = null,
  previousCropMargin = null,
  fromProduction = false,
  fromSynthesis = false,
  fromParcels = false,
}) => {
  const { culturesColors } = useContext(ExploitationContext);
  const [culturesByYear, setCulturesByYear] = useState([]);
  const [totalSurface, setTotalSurface] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (data) {
      const { allCulturesByYear, totalSurfaceIncrement } = loadResultMapTabData(
        data,
        culturesColors,
        minYear,
        maxYear,
        fromParcels ? true : false,
      );

      allCulturesByYear.forEach((yearData) => {
        const distribution = [
          ...(previousCropMargin ?? []),
          ...(suggestions?.flatMap((s) => s.culturesDistribution ?? []) ?? []),
        ];
        yearData.cultures = yearData.cultures.map((culture) => ({
          ...culture,
          capCategory: distribution.find((d) => d.culture?.id === culture.id)?.culture?.capCategory,
        }));
      });

      setCulturesByYear(allCulturesByYear);
      setTotalSurface(totalSurfaceIncrement);
    }
  }, [data, year, yearIndex]);

  return (
    <div className="cultures-map-tab-container">
      {!fromProduction && (
        <YearsMapTab year={year} minYear={minYear} maxYear={maxYear} setYear={setYear} />
      )}
      <div
        className={
          fromProduction ? 'table-container sticky-table table-production' : 'table-container'
        }
      >
        <div className="table-wrapper">
          {!culturesByYear?.some((culture) => culture.year === year - 1) && (
            <div className="no-cultures-message">{t('simulation.common.noCulturesForYear')}</div>
          )}
          {culturesByYear?.some((culture) => culture.year === year - 1) && (
            <table
              className={
                isDynamic ? 'cultures-map-tab-table dynamic-table' : 'cultures-map-tab-table'
              }
            >
              <thead
                className={
                  fromProduction ? 'table_head sticky-table' : 'table_head table-synthesis-sticky'
                }
              >
                <tr>
                  {header.map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className={
                        header === t('column.marginPerHa') ? 'last_column_name' : 'column_name'
                      }
                      colSpan={
                        header === t('simulation.common.margin') || header === t('common.surface') ? 2 : 1
                      }
                    >
                      <span>{header}</span>
                    </th>
                  ))}
                </tr>
                {header.includes(t('common.surface')) && (
                  <tr className="column_subName">
                    <th colSpan={2}></th>
                    <th colSpan={1}>(ha)</th>
                    <th colSpan={1}>(%)</th>
                    {header.includes(t('column.marginPerHa')) && <th colSpan={2}></th>}
                  </tr>
                )}
                {header.includes(t('simulation.common.margin')) && (
                  <tr className="column_subName">
                    <th colSpan={2}></th>
                    <th colSpan={1}>(€)</th>
                    <th colSpan={1}>(€ / ha)</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {(() => {
                  const sortedCultures = sortList(
                    culturesByYear,
                    fromProduction ? year + (yearIndex ?? 0) - 1 : year - 1,
                  )?.filter((culture) => culture.id !== undefined);

                  const mainCultures =
                    sortedCultures?.filter((culture) => culture.type !== 'intermediate') || [];
                  const intermediateCultures =
                    sortedCultures?.filter((culture) => culture.type === 'intermediate') || [];

                  const renderCultureRow = (culture, i) => (
                    <tr
                      key={i}
                      className="tr"
                      style={{
                        opacity: parcelHoverCultureOpacity(culture.name, parcelHoverCultureName),
                      }}
                      {...(isDynamic && {
                        onMouseEnter: () => cultureHover(culture.name, parentCallback),
                        onMouseLeave: () => cultureHover('null', parentCallback),
                      })}
                    >
                      <td key={getColorByName(culture.name, culturesColors)}>
                        <div
                          className="culture-color"
                          style={{ background: '#' + getColorByName(culture.name, culturesColors) }}
                        ></div>
                      </td>

                      {fromProduction && (
                        <>
                          <td className="culture-line-clamp" key={culture.name}>
                            {culture.name}{' '}
                            {culture?.organicState === 'conversion' ? (
                              <SyncAltIcon className="icon" />
                            ) : culture?.organicState === 'organic' ? (
                              <img src={bioLogo} alt={t('alt.bioLogo')} className="icon" />
                            ) : null}
                          </td>
                          <td key={'marge_total'}>
                            {getCultureGrossMargin(
                              culture,
                              previousCropMargin,
                              (yearIndex ?? 0) - 1,
                              suggestions,
                            )}
                          </td>
                          <td key={'marge_ha'}>
                            {getCultureGrossMargin(
                              culture,
                              previousCropMargin,
                              (yearIndex ?? 0) - 1,
                              suggestions,
                              culture.surface,
                            )}
                          </td>
                        </>
                      )}
                      {(fromSynthesis || fromParcels) && (
                        <>
                          <td key={culture.name}>
                            {culture.name ?? t('message.noCultureAssociated')}
                          </td>
                          <td key={culture.surface}>{Math.round(culture.surface * 100) / 100}</td>
                          <td key={'percent'}>{getPercentage(culture.surface, totalSurface)}</td>
                          {fromSynthesis && (
                            <td key={'marge'}>
                              {getCultureGrossMargin(
                                culture,
                                previousCropMargin,
                                yearIndex,
                                suggestions,
                                culture.surface,
                              ) ?? '-'}
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  );

                  return (
                    <React.Fragment>
                      {mainCultures.map((culture, i) => renderCultureRow(culture, i))}

                      {mainCultures.length > 0 && (
                        <tr className="subtotal-row">
                          <td></td>
                          <td className="subtotal-label">{t('title.mainCulturesSubtotal')}</td>
                          <td className="subtotal-value">
                            {Math.round(
                              mainCultures.reduce((sum, c) => sum + (c.surface || 0), 0) * 100,
                            ) / 100}
                          </td>
                          <td></td>
                          {fromSynthesis && <td></td>}
                        </tr>
                      )}

                      {intermediateCultures.length > 0 && (
                        <tr className="section-header-row">
                          <th colSpan={header.length + 1} className="section-header">
                            {t('common.interculture')}
                          </th>
                        </tr>
                      )}

                      {intermediateCultures.map((culture, i) =>
                        renderCultureRow(culture, mainCultures.length + i),
                      )}

                      {intermediateCultures.length > 0 && (
                        <tr className="subtotal-row subtotal-row--intermediate">
                          <td></td>
                          <td className="subtotal-label">{t('title.interculturesSubtotal')}</td>
                          <td className="subtotal-value">
                            {Math.round(
                              intermediateCultures.reduce((sum, c) => sum + (c.surface || 0), 0) *
                                100,
                            ) / 100}
                          </td>
                          <td></td>
                          {fromSynthesis && <td></td>}
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })()}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapTab;
